<template>
  <section class="porsche-radius">
    <commonModal
      :show="showErrorModal"
      :actionButton="true"
      :options="{
        title: 'Sorry!',
        description: `We were unable to locate your quote using that information. To try again, please close this window, or you may click the button below to begin a new quote.`,
        buttonLabel: 'New Quote'
      }"
      @close="clickedCloseOnModal"
    ></commonModal>
    <form class="has-placeholders" method="post" action="/">
      <fieldset>
        <legend class="is-bold" v-if="!openRetrieveQuote">{{ title }}</legend>
        <p v-show="showPhone" class="is-paddingless" style="margin:0">
          Get a quote:
          <a style="text-decoration:underline;" :href="'tel:1-' + $phoneNumber"
            >1-{{ $phoneNumber }}</a
          >
        </p>
        <div class="select-wrapper -spacing-static-c" v-if="!openRetrieveQuote">
          <p v-html="subtitle" style="margin: 0 auto; font-size: 0.85em;"></p>
          <v-select
            v-if="showState"
            v-model="quoteState"
            :searchable="true"
            :options="stateOptions"
            id="state"
            name="state"
            placeholder="Select your state"
          ></v-select>
        </div>
        <div class="buttons-wrapper">
          <button
            @click="startQuote"
            type="button"
            class="button-primary  button-primary--black -spacing-c"
            v-if="!openRetrieveQuote"
          >
            <span class="button-primary__label no-icon"
              >Start a tailored quote</span
            >
          </button>
          <button
            v-if="!openRetrieveQuote"
            @click="resumeQuote"
            type="button"
            class="button-primary  button-primary--black -spacing-c second-button"
          >
            <span class="button-primary__label no-icon">
              Retrieve my quote
            </span>
          </button>
        </div>
        <legend class="is-bold" v-if="openRetrieveQuote">
          Retrieve your quote
        </legend>
        <div v-if="openRetrieveQuote">
          <label class="input -spacing-f">
            <input
              v-model="emailAddress"
              name="emailAddress"
              :class="{
                input__field: true,
                error: errors.has('emailAddress')
              }"
              placeholder="Email Address"
              v-validate="'required|email'"
            />

            <span class="input__label">Email Address</span>
          </label>
          <div class="limit-container">
            <label class="input -spacing-static-c">
              <masked-input
                :class="{
                  input__field: true,
                  error: errors.has('dateOfBirth')
                }"
                type="text"
                name="dateOfBirth"
                placeholder="Date of birth (MM/DD/YYYY)"
                v-model="dateOfBirth"
                @blur="checkWorking('dateOfBirth', dateOfBirth)"
                :keepCharPositions="true"
                v-validate="'required|validate18YrsOld|isLeapYear'"
                :placeholderChar="'_'"
                :pipe="autoCorrectedDatePipe"
                :mask="[
                  /\d/,
                  /\d/,
                  '/',
                  /\d/,
                  /\d/,
                  '/',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/
                ]"
                :guide="true"
              ></masked-input>
              <span class="input__label">Date of birth (MM/DD/YYYY)</span>
            </label>
            <b-icon pack="fas" icon="calendar" class="calendar-icon"></b-icon>
            <b-datepicker
              name="dateOfBirth1"
              @input="changedDOB"
              v-model="selectedDate"
              :max-date="validationOptions.atleastEighteenYrsOld"
              :focused-date="validationOptions.atleastEighteenYrsOld"
              :mobile-native="false"
              data-vv-validate-on="input|blur"
            ></b-datepicker>
          </div>
          <label class="input -spacing-f">
            <input
              v-model="zipCode"
              name="zipcode"
              maxlength="5"
              @paste="onPaste"
              @input="onlyNumbers"
              :class="{
                input__field: true,
                error: errors.has('zipcode')
              }"
              placeholder="ZIP Code"
              v-validate="'required|numeric|length:5'"
              data-vv-validate-on="input|blur"
            />

            <span class="input__label">ZIP Code</span>
          </label>
        </div>
        <button
          v-if="openRetrieveQuote"
          @click="openRetrieveQuote = !openRetrieveQuote"
          type="button"
          class="button-primary button-primary--black -spacing-c"
        >
          <span class="button-primary__label no-icon">Back</span>
        </button>
        <button
          v-if="openRetrieveQuote"
          @click="resumeQuote"
          type="button"
          class="button-primary  button-primary--black -spacing-c"
        >
          <span class="button-primary__label no-icon">
            See my quote
          </span>
        </button>
      </fieldset>
    </form>
  </section>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from 'vuex';
import { canUseAdvertising } from '@/utils/tracking';
import commonModal from '../shared/commonModal';
import moment from 'moment';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';

export default {
  name: 'HomepageQuote',
  props: {
    showPhone: {
      type: Boolean,
      default: true
    },
    title: {
      type: String
    },
    subtitle: {
      type: String
    },
    showState: {
      type: Boolean,
      example: true
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    autoCorrectedDatePipe() {
      return createAutoCorrectedDatePipe('mm/dd/yyyy');
    }
  },
  components: {
    commonModal
  },
  methods: {
    ...mapMutations([
      'updateForm',
      'setSelectedPolicy',
      'updateQuoteResponse',
      'toggleLoader'
    ]),
    ...mapActions(['fetchQuote']),
    FLOOD1() {
      if (
        process.env.VUE_APP_ENV.toLowerCase() !== 'production' &&
        process.env.VUE_APP_ENV.toLowerCase() !== 'prod'
      ) {
        return;
      }
      if (!canUseAdvertising()) {
        return;
      }
      // console.log('Floodlight 1 Active');
      // USA_Porsche_DCM_Floodlight_PAI_Homepage_StartQuote_ButtonClick
      let axel = Math.random() + '';
      let a = axel * 10000000000000;
      let flDiv = document.body.appendChild(document.createElement('div'));
      let GDPR = '${GDPR}';
      let GDPR_CONSENT_755 = '${GDPR_CONSENT_755}';
      flDiv.setAttribute('id', process.env.VUE_APP_FLOODLIGHT_ID);
      flDiv.style.position = 'absolute';
      flDiv.style.top = '0';
      flDiv.style.left = '0';
      flDiv.style.width = '1px';
      flDiv.style.height = '1px';
      flDiv.style.display = 'none';
      flDiv.innerHTML = `<iframe id=${process.env.VUE_APP_FLOODLIGHT_ID +
        '_FLIframe2'} src="https://5368208.fls.doubleclick.net/activityi;src=5368208;type=porsc00c;cat=usa_p001;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=${a}?" width="1" height="1" frameborder="0"></iframe>`;
    },
    startQuote() {
      this.FLOOD1();
      if (
        this.quoteState === null ||
        this.quoteState.value === 'AZ' ||
        this.quoteState.value === 'CO' ||
        this.quoteState.value === 'FL' ||
        this.quoteState.value === 'GA' ||
        this.quoteState.value === 'IL' ||
        this.quoteState.value === 'OH' ||
        this.quoteState.value === 'OR' ||
        this.quoteState.value === 'TN' ||
        this.quoteState.value === 'TX' ||
        this.quoteState.value === 'UT'
      ) {
        let payload = {
          info: {
            state: this.quoteState
              ? { label: this.quoteState.label, value: this.quoteState.value }
              : null,
            zipCode: null
          },
          vehicles: [],
          drivers: [],
          coverage: {}
        };
        this.updateForm(payload);
        this.$router.push('/receive-a-quote');
      } else if (this.quoteState.value === 'CA') {
        this.$router.push('/unavailable?state=CA');
      } else if (
        this.quoteState.value === 'CA' ||
        this.quoteState.value === 'OH' ||
        this.quoteState.value === 'PA' ||
        this.quoteState.value === 'WI'
      ) {
        this.$router.push('/unavailable?not-writing');
      } else {
        this.$router.push({
          name: 'Unavailable',
          query: { state: this.quoteState.label }
        });
      }
    },
    checkWorking(key, value) {
      let validator = this.$validator;
      validator.validate(key, value);
    },

    FLOOD2() {
      if (
        process.env.VUE_APP_ENV.toLowerCase() !== 'production' &&
        process.env.VUE_APP_ENV.toLowerCase() !== 'prod'
      ) {
        return;
      }
      if (!canUseAdvertising()) {
        return;
      }
      // console.log('Floodlight 2 Active');
      // USA_Porsche_DCM_Floodlight_PAI_Homepage_RetrieveQuote_ButtonClick
      let axel = Math.random() + '';
      let a = axel * 10000000000000;
      let flDiv = document.body.appendChild(document.createElement('div'));
      let GDPR = '${GDPR}';
      let GDPR_CONSENT_755 = '${GDPR_CONSENT_755}';
      flDiv.setAttribute('id', process.env.VUE_APP_FLOODLIGHT_ID);
      flDiv.style.position = 'absolute';
      flDiv.style.top = '0';
      flDiv.style.left = '0';
      flDiv.style.width = '1px';
      flDiv.style.height = '1px';
      flDiv.style.display = 'none';
      flDiv.innerHTML = `<iframe id=${process.env.VUE_APP_FLOODLIGHT_ID +
        '_FLIframe2'} src="https://5368208.fls.doubleclick.net/activityi;src=5368208;type=porsc00c;cat=usa_p000;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=${a}?" width="1" height="1" frameborder="0"></iframe>`;
    },

    resumeQuote() {
      this.FLOOD2();
      if (!this.isLoggedIn && !this.openRetrieveQuote) {
        this.openRetrieveQuote = true;
        return;
      }
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.isLoggedIn) {
            this.$router.push('/account/lookup-quotes');
          } else {
            //guest user case goes here
            this.fetchPolicy();
          }
        } else {
          event.preventDefault();
        }
      });
    },

    fetchPolicy() {
      if (this.emailAddress && this.dateOfBirth && this.zipCode) {
        this.toggleLoader({
          loaderText: 'Please wait while we retrieve your information.'
        });
        let request = {
          emailAddress: this.emailAddress,
          dob: moment(this.dateOfBirth, 'MM/DD/YYYY').format('YYYY-MM-DD'),
          zip: this.zipCode
        };
        this.fetchQuote(request)
          .then(() => {
            this.toggleLoader();
            this.$router.push({
              path: '/receive-a-quote',
              query: {
                email: this.emailAddress.toLowerCase(),
                dob: moment(this.dateOfBirth, 'MM/DD/YYYY').format(
                  'YYYY-MM-DD'
                ),
                zipcode: this.zipCode
              }
            });
          })
          .catch(() => {
            this.toggleLoader();
            this.openRetrieveQuote = !this.openRetrieveQuote;
            this.$snackbar.open({
              type: 'is-danger',
              message: `We were unable to locate your quote using that information. To try again, please close this window, or you may begin a new quote.`,
              position: 'is-bottom'
            });
            this.showErrorModal = !this.showErrorModal;
          });
      }
    },

    clickedCloseOnModal(value) {
      this.showErrorModal = false;
      if (value === 1) {
        this.$router.push('/receive-a-quote');
      }
    },

    onPaste(event) {
      event.clipboardData.getData('text/plain').slice(0, 5);
    },

    onlyNumbers() {
      this.zipCode = this.zipCode.toString().replace(/[^0-9]/g, '');
    },
    changedDOB(event) {
      this.dateOfBirth = moment(event).format('MM/DD/YYYY');
      this.selectedDate = null;
      this.$el.querySelector('[name="dateOfBirth"]').focus();
      this.$validator.validate('dateOfBirth', this.dateOfBirth);
    }
  },
  data: () => ({
    quoteState: null,
    stateOptions: [
      {
        label: 'Alabama',
        value: 'AL'
      },
      {
        label: 'Alaska',
        value: 'AK'
      },
      {
        label: 'Arizona',
        value: 'AZ'
      },
      {
        label: 'Arkansas',
        value: 'AR'
      },
      {
        label: 'California',
        value: 'CA'
      },
      {
        label: 'Colorado',
        value: 'CO'
      },
      {
        label: 'Connecticut',
        value: 'CT'
      },
      {
        label: 'Delaware',
        value: 'DE'
      },
      {
        label: 'District Of Columbia',
        value: 'DC'
      },
      {
        label: 'Florida',
        value: 'FL'
      },
      {
        label: 'Georgia',
        value: 'GA'
      },
      {
        label: 'Hawaii',
        value: 'HI'
      },
      {
        label: 'Idaho',
        value: 'ID'
      },
      {
        label: 'Illinois',
        value: 'IL'
      },
      {
        label: 'Indiana',
        value: 'IN'
      },
      {
        label: 'Iowa',
        value: 'IA'
      },
      {
        label: 'Kansas',
        value: 'KS'
      },
      {
        label: 'Kentucky',
        value: 'KY'
      },
      {
        label: 'Louisiana',
        value: 'LA'
      },
      {
        label: 'Maine',
        value: 'ME'
      },
      {
        label: 'Maryland',
        value: 'MD'
      },
      {
        label: 'Massachusetts',
        value: 'MA'
      },
      {
        label: 'Michigan',
        value: 'MI'
      },
      {
        label: 'Minnesota',
        value: 'MN'
      },
      {
        label: 'Mississippi',
        value: 'MS'
      },
      {
        label: 'Missouri',
        value: 'MO'
      },
      {
        label: 'Montana',
        value: 'MT'
      },
      {
        label: 'Nebraska',
        value: 'NE'
      },
      {
        label: 'Nevada',
        value: 'NV'
      },
      {
        label: 'New Hampshire',
        value: 'NH'
      },
      {
        label: 'New Jersey',
        value: 'NJ'
      },
      {
        label: 'New Mexico',
        value: 'NM'
      },
      {
        label: 'New York',
        value: 'NY'
      },
      {
        label: 'North Carolina',
        value: 'NC'
      },
      {
        label: 'North Dakota',
        value: 'ND'
      },
      {
        label: 'Ohio',
        value: 'OH'
      },
      {
        label: 'Oklahoma',
        value: 'OK'
      },
      {
        label: 'Oregon',
        value: 'OR'
      },
      {
        label: 'Pennsylvania',
        value: 'PA'
      },
      {
        label: 'Rhode Island',
        value: 'RI'
      },
      {
        label: 'South Carolina',
        value: 'SC'
      },
      {
        label: 'South Dakota',
        value: 'SD'
      },
      {
        label: 'Tennessee',
        value: 'TN'
      },
      {
        label: 'Texas',
        value: 'TX'
      },
      {
        label: 'Utah',
        value: 'UT'
      },
      {
        label: 'Vermont',
        value: 'VT'
      },
      {
        label: 'Virginia',
        value: 'VA'
      },
      {
        label: 'Washington',
        value: 'WA'
      },
      {
        label: 'West Virginia',
        value: 'WV'
      },
      {
        label: 'Wisconsin',
        value: 'WI'
      },
      {
        label: 'Wyoming',
        value: 'WY'
      }
    ],
    openRetrieveQuote: false,
    emailAddress: null,
    dateOfBirth: '',
    zipCode: null,
    selectedDate: null,
    showErrorModal: false,
    validationOptions: {
      dateOfBirth: {
        date: true,
        datePattern: ['m', 'd', 'Y'],
        delimiter: '/'
      },
      atleastEighteenYrsOld: new Date(
        new Date().setFullYear(new Date().getFullYear() - 18)
      )
    }
  })
};
</script>

<style lang="scss" scoped>
section {
  background-color: $porcelain;
  padding: 32px 30px;

  @media (max-width: 480px) {
    padding: 32px 15px;
  }
}

legend {
  color: #000;
  font-family: $family-sans-serif-thin;
  font-size: 1.5rem;
}

.is-bold {
  font-weight: 600;
}

form {
  label {
    margin-top: 30px;
  }
  .select-wrapper {
    margin: 0.5rem 0;

    @media (max-width: 480px) {
      margin: 0.25rem 0;
    }
  }
  .buttons-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    @media (max-width: 420px) {
      padding: 0;
    }

    button {
      text-align: center;
    }
  }
  .button-primary {
    margin: 0.5rem 0.5rem 0.5rem 0;

    @media (max-width: 480px) {
      margin: 0.25rem 0.25rem 0.25rem 0;
    }
  }
  .back-button {
    background-color: transparent;
    border: solid 1px #403f45;
    border-radius: 4px;
    color: #403f45;
  }
  .button-primary + .button-primary {
    @media (min-width: 760px) and (max-width: 1140px) {
      min-width: 165px;
      text-align: center;
    }
  }
  hr {
    background: #403f45;
    height: 1px;
  }
}
</style>
